import React, { createContext, useContext, useRef, useState, useCallback } from 'react';

// Context para manejar el estado global de videos
const VideoManagerContext = createContext();

// Hook para usar el VideoManager
export const useVideoManager = () => {
  const context = useContext(VideoManagerContext);
  if (!context) {
    throw new Error('useVideoManager must be used within a VideoManagerProvider');
  }
  return context;
};

// Provider del VideoManager
export const VideoManagerProvider = ({ children }) => {
  const [currentPlayingVideo, setCurrentPlayingVideo] = useState(null);
  const [currentLoadingVideo, setCurrentLoadingVideo] = useState(null); // Video que está cargando
  const videoRefs = useRef(new Map());

  // Registrar un video en el manager
  const registerVideo = useCallback((videoId, videoRef) => {
    videoRefs.current.set(videoId, videoRef);
  }, []);

  // Desregistrar un video
  const unregisterVideo = useCallback((videoId) => {
    videoRefs.current.delete(videoId);
  }, []);

  // Pausar todos los videos excepto el especificado
  const pauseAllExcept = useCallback(async (exceptVideoId) => {
    // Crear un array de promesas para pausar todos los videos en paralelo
    const pausePromises = [];
    
    for (const [videoId, videoRef] of videoRefs.current.entries()) {
      // IMPORTANTE: NO pausar el video que queremos reproducir
      if (videoId === exceptVideoId) {
        continue;
      }
      
      if (videoRef?.current) {
        const video = videoRef.current;
        // Solo pausar si está reproduciéndose
        if (!video.paused) {
          pausePromises.push(
            (async () => {
              try {
                // Pausar el video
                video.pause();
                // No necesitamos esperar - pause() es síncrono
              } catch (error) {
                // Ignorar errores de pausa - no es crítico
                console.debug(`Error pausing video ${videoId}:`, error);
        }
            })()
          );
        }
      }
    }
    
    // Esperar a que todas las pausas se completen (si hay alguna)
    if (pausePromises.length > 0) {
      await Promise.all(pausePromises);
      // Pequeña pausa para asegurar que las pausas se completen antes de reproducir
      await new Promise(resolve => setTimeout(resolve, 50));
    }
  }, []);

  // Reproducir un video específico (pausando todos los demás)
  const playVideo = useCallback(async (videoId) => {
    try {
      const videoRef = videoRefs.current.get(videoId);
      if (!videoRef?.current) {
        console.warn(`Video ${videoId} not found`);
        return;
      }

      const video = videoRef.current;
      
      // IMPORTANTE: Pausar otros videos PRIMERO (esto NO afecta el video actual)
      await pauseAllExcept(videoId);
      
      // Verificar que el video aún existe
      if (!videoRef.current || videoRef.current !== video) {
        console.warn(`Video ${videoId} was removed or changed`);
        return;
      }
      
      // Si el video ya está reproduciéndose, solo actualizar el estado
      if (!video.paused && video.readyState >= 2) {
        setCurrentPlayingVideo(videoId);
        // Limpiar estado de carga si estaba cargando
        if (currentLoadingVideo === videoId) {
          setCurrentLoadingVideo(null);
        }
        return;
      }
      
      // Verificar que el video esté listo para reproducir
      // Si no está listo, el componente VideoCardFallback ya debería haberlo preparado
      // pero verificamos de todos modos por seguridad
      if (video.readyState < 1) {
        // Esperar a que tenga al menos metadata (máximo 2 segundos)
        await new Promise((resolve, reject) => {
          let resolved = false;
          let timeoutId;
          
          const cleanup = () => {
            video.removeEventListener('loadedmetadata', onLoadedMetadata);
            video.removeEventListener('error', onError);
            if (timeoutId) clearTimeout(timeoutId);
          };
          
          const onLoadedMetadata = () => {
            if (resolved) return;
            resolved = true;
            cleanup();
            resolve();
          };
          
          const onError = () => {
            if (resolved) return;
            resolved = true;
            cleanup();
            reject(new Error('Video failed to load'));
          };
          
          video.addEventListener('loadedmetadata', onLoadedMetadata);
          video.addEventListener('error', onError);
          
          // Timeout de seguridad (2 segundos)
          timeoutId = setTimeout(() => {
            if (resolved) return;
            resolved = true;
            cleanup();
            if (video.readyState >= 1) {
              resolve();
            } else {
              reject(new Error('Video load timeout'));
            }
          }, 2000);
        });
      }
      
      // Verificar nuevamente que el video aún existe
      if (!videoRef.current || videoRef.current !== video) {
        return;
      }
      
      // Intentar reproducir el video
      // IMPORTANTE: Si hay un AbortError, es porque algo pausó el video
      // En ese caso, NO intentar de nuevo automáticamente para evitar loops infinitos
      const playPromise = video.play();
      if (playPromise !== undefined) {
        try {
          await playPromise;
          // Solo actualizar el estado si la reproducción fue exitosa
          if (!video.paused && videoRef.current === video) {
            setCurrentPlayingVideo(videoId);
            // Limpiar estado de carga cuando empieza a reproducir
            setCurrentLoadingVideo(null);
          }
        } catch (playError) {
          // Si el error es AbortError, puede ser porque:
          // 1. El usuario hizo click en otro video (esperado)
          // 2. Algún código pausó el video (no esperado, pero no crítico)
          // No intentar de nuevo automáticamente - dejar que el usuario intente de nuevo
          if (playError.name !== 'AbortError') {
            throw playError;
          }
          // Para AbortError, solo loguear en debug mode
          console.debug(`Video ${videoId} play was interrupted:`, playError.message);
        }
      }
    } catch (error) {
      // Si el error es porque se interrumpió o no está permitido, no es crítico
      if (error.name === 'AbortError' || error.name === 'NotAllowedError') {
        console.debug(`Video ${videoId} play was interrupted or not allowed:`, error.message);
      } else {
      console.warn(`Error playing video ${videoId}:`, error);
      }
      // Limpiar estado de carga si hay error
      if (currentLoadingVideo === videoId) {
        setCurrentLoadingVideo(null);
      }
    }
  }, [pauseAllExcept, currentLoadingVideo]);

  // Pausar un video específico
  const pauseVideo = useCallback((videoId) => {
    const videoRef = videoRefs.current.get(videoId);
    if (videoRef?.current) {
      videoRef.current.pause();
      if (currentPlayingVideo === videoId) {
        setCurrentPlayingVideo(null);
      }
      // Si el video estaba cargando, dejar de cargar
      if (currentLoadingVideo === videoId) {
        setCurrentLoadingVideo(null);
      }
    }
  }, [currentPlayingVideo, currentLoadingVideo]);

  // Marcar un video como cargando (para pausar carruseles)
  // OPTIMIZACIÓN: Si otro video está cargando, limpiar el estado del video anterior
  const setLoadingVideo = useCallback((videoId) => {
    // Si hay otro video cargando, limpiarlo primero
    if (currentLoadingVideo !== null && currentLoadingVideo !== videoId) {
      // Limpiar el video anterior que estaba cargando
      setCurrentLoadingVideo(null);
    }
    // Marcar el nuevo video como cargando
    setCurrentLoadingVideo(videoId);
  }, [currentLoadingVideo]);

  // Limpiar estado de carga de un video
  const clearLoadingVideo = useCallback((videoId) => {
    if (currentLoadingVideo === videoId) {
      setCurrentLoadingVideo(null);
    }
  }, [currentLoadingVideo]);

  // Pausar todos los videos
  const pauseAllVideos = useCallback(async () => {
    for (const [videoId, videoRef] of videoRefs.current.entries()) {
      if (videoRef?.current) {
        try {
          videoRef.current.pause();
        } catch (error) {
          console.warn(`Error pausing video ${videoId}:`, error);
        }
      }
    }
    setCurrentPlayingVideo(null);
    setCurrentLoadingVideo(null);
  }, []);

  const value = {
    currentPlayingVideo,
    currentLoadingVideo, // Video que está cargando
    registerVideo,
    unregisterVideo,
    playVideo,
    pauseVideo,
    pauseAllVideos,
    pauseAllExcept,
    setLoadingVideo, // Marcar video como cargando
    clearLoadingVideo // Limpiar estado de carga
  };

  return (
    <VideoManagerContext.Provider value={value}>
      {children}
    </VideoManagerContext.Provider>
  );
};

export default VideoManagerProvider;

